import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BILLING_CLASS_LABEL, CHANGE_STAGE_LABEL, billingClassFor, estimateProblems, estimateTotals,
  type ChangeEstimate, type ChangeStage, type Discipline
} from '@veta-desk/domain';
import { Shell } from '../components/Shell';
import { ChangeQuotePanel } from '../components/ChangeQuotePanel';
import { EvolutivePanel } from '../components/EvolutivePanel';
import { ChangeDeliveryPanel } from '../components/ChangeDeliveryPanel';
import { ProgressSteps, type ProgressNode } from '../components/StageLine';
import { api, ApiError, type ChangeDetail as Detail, type Meta } from '../lib/api';
import { dateTime } from '../lib/format';

// Veta Change: detalle del cambio. M1 = estimación del consultor por rubro × disciplina; M4 = entrega.

const DELIVERY_STAGES: ChangeStage[] = ['approved', 'development', 'uat', 'correction', 'deployment', 'closed'];

const field = 'h-9 w-full rounded-md border border-[#D6D3CD] bg-white px-2.5 text-sm focus:border-accent focus:outline-none disabled:bg-paper';
const area = 'w-full rounded-md border border-[#D6D3CD] bg-white px-2.5 py-2 text-sm focus:border-accent focus:outline-none disabled:bg-paper';
const btnPrimary = 'inline-flex h-9 items-center rounded-md bg-accent px-4 text-sm font-semibold text-white hover:bg-accent-dark disabled:opacity-50';
const btnSecondary = 'inline-flex h-9 items-center rounded-md border border-[#D6D3CD] bg-white px-3 text-sm text-[#3F3D39] hover:bg-paper disabled:opacity-50';

const STEPS: { label: string; stages: ChangeStage[] }[] = [
  { label: 'Estimación', stages: ['estimating'] },
  { label: 'Revisión del gestor', stages: ['manager_review'] },
  { label: 'Aprobación del cliente', stages: ['pending_approval'] },
  { label: 'Desarrollo', stages: ['approved', 'development'] },
  { label: 'Pruebas del usuario', stages: ['uat', 'correction'] },
  { label: 'Producción', stages: ['deployment'] },
  { label: 'Cierre', stages: ['closed'] }
];

const CLASS_TONE = {
  contract: 'bg-accent-soft text-accent-dark',
  additional: 'bg-[#FFF4E5] text-[#6A3A00]',
  project: 'bg-[#FBEAEA] text-[#8F1D1D]'
} as const;

/** Nodos de la línea de etapas del cambio, con el mismo estándar visual que la del ticket. */
function changeNodes(d: Detail): ProgressNode[] {
  const finals: Partial<Record<ChangeStage, string>> = { rejected: 'Rechazado', cancelled: 'Cancelado', converted: 'Convertido a proyecto' };
  const alt = finals[d.stage];
  const idx = STEPS.findIndex(s => s.stages.includes(d.stage));
  const round = d.delivery?.uat?.round;
  if (alt) {
    return [{ key: 'start', label: STEPS[0]!.label, kind: 'faded', note: '' }, { key: d.stage, label: alt, kind: 'end', note: 'fin' }];
  }
  return STEPS.map((s, i) => {
    if (i < idx) return { key: s.label, label: s.label, kind: 'past', note: '' };
    if (i > idx) return { key: s.label, label: s.label, kind: 'future', note: '' };
    if (d.stage === 'closed') return { key: s.label, label: s.label, kind: 'end', note: 'Cerrado' };
    if (d.stage === 'correction') return { key: s.label, label: s.label, kind: 'hold', note: `En corrección${round ? ` · ronda ${round}` : ''}` };
    const note = d.stage === 'uat' && round ? `Ronda ${round}` : d.stage === 'approved' ? 'Por iniciar' : d.stage === 'deployment' && d.delivery?.production ? 'Por cerrar' : 'Actual';
    return { key: s.label, label: s.label, kind: 'now', note };
  });
}

interface FormState extends Omit<ChangeEstimate, 'abapEstimatedBy'> { abapEstimatedById: string | null }

const toForm = (e: ChangeEstimate): FormState => ({ ...e, abapEstimatedById: e.abapEstimatedBy?.id ?? null });

export default function ChangeDetail() {
  const { number = '' } = useParams();
  const queryClient = useQueryClient();
  const q = useQuery({ queryKey: ['change', number], queryFn: () => api<Detail>(`/change/${number}`) });
  const meta = useQuery({ queryKey: ['meta'], queryFn: () => api<Meta>('/meta'), staleTime: 5 * 60_000 });
  const [form, setForm] = useState<FormState | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [showEstimate, setShowEstimate] = useState<boolean | null>(null);
  useEffect(() => { if (q.data) setForm(toForm(q.data.draft)); }, [q.data]);

  const onDone = (d: Detail, text: string) => {
    queryClient.setQueryData(['change', number], d);
    void queryClient.invalidateQueries({ queryKey: ['changes'] });
    void queryClient.invalidateQueries({ queryKey: ['ticket', number] });
    setMsg({ ok: true, text });
  };
  const onError = (e: unknown) => setMsg({ ok: false, text: e instanceof ApiError ? e.message : 'Algo salió mal.' });
  const body = () => JSON.stringify(form);
  const saveDraft = useMutation({ mutationFn: () => api<Detail>(`/change/${number}/estimate`, { method: 'PUT', body: body() }), onSuccess: d => onDone(d, 'Borrador guardado.'), onError });
  const submit = useMutation({ mutationFn: () => api<Detail>(`/change/${number}/estimate/submit`, { method: 'POST', body: body() }), onSuccess: d => onDone(d, 'Estimación enviada al gestor del servicio.'), onError });

  const live = useMemo(() => {
    if (!form || !q.data) return null;
    const est: ChangeEstimate = { ...form, abapEstimatedBy: null };
    const totals = estimateTotals(est);
    return { totals, cls: billingClassFor(totals.total, q.data.sizing), problems: estimateProblems(est) };
  }, [form, q.data]);

  if (q.isLoading || (!form && q.data)) return <Shell><p role="status" className="py-10 text-center text-sm text-muted">Cargando…</p></Shell>;
  if (q.isError || !q.data || !form || !live) {
    const basic = q.error instanceof ApiError && q.error.code === 'BASIC_FLOW';
    return (
      <Shell>
        <div className="rounded-lg border border-line bg-white p-8 text-center">
          <p className="text-sm">{basic ? (q.error as ApiError).message : 'No encontramos ese cambio.'}</p>
          <Link to={basic ? `/tickets/${number}` : '/cambios'} className="mt-3 inline-block text-sm text-accent underline">{basic ? 'Ir al ticket' : 'Volver a Cambios'}</Link>
        </div>
      </Shell>
    );
  }

  const d = q.data;
  const editable = d.permissions.estimate;
  const hours = (phase: string, disc: Discipline) => form.lines.find(l => l.phase === phase && l.discipline === disc)?.hours ?? 0;
  const setHours = (phase: string, disc: Discipline, value: number) => {
    setMsg(null);
    const others = form.lines.filter(l => !(l.phase === phase && l.discipline === disc));
    setForm({ ...form, lines: value > 0 ? [...others, { phase, discipline: disc, hours: value }] : others });
  };
  const set = (patch: Partial<FormState>) => { setMsg(null); setForm({ ...form, ...patch }); };
  // Ya en la entrega, la estimación se pliega (se consulta con "Ver").
  const inEstimation = !DELIVERY_STAGES.includes(d.stage);
  const estimateOpen = showEstimate ?? inEstimation;
  const phaseTotal = (phase: string) => hours(phase, 'functional') + (form.requiresAbap ? hours(phase, 'abap') : 0);

  return (
    <Shell>
      <div className="flex flex-col gap-5">
        <Link to="/cambios" className="text-sm text-accent hover:underline">← Cambios</Link>
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="flex flex-wrap items-center gap-2 text-sm">
              <Link to={`/tickets/${d.ticket.number}`} className="font-mono font-medium text-accent hover:underline">{d.ticket.number}</Link>
              <span className="rounded bg-[#EDEBE7] px-1.5 py-0.5 text-xs font-semibold text-muted">Veta Change</span>
              <span className="rounded-full bg-accent-soft px-2 py-0.5 text-xs font-semibold text-accent-dark">{CHANGE_STAGE_LABEL[d.stage]}</span>
            </p>
            <h1 className="mt-1.5 text-2xl font-semibold leading-tight">{d.ticket.subject}</h1>
            <p className="mt-1 text-sm text-muted">{d.ticket.organizationName}{d.ticket.initiativeCode ? ` · ${d.ticket.initiativeCode}` : ''} · dueño: {d.ticket.assigneeName ?? 'sin asignar'} · solicita {d.ticket.requesterName}</p>
          </div>
        </header>

        <div className="rounded-lg border border-line bg-white px-4 pb-3 pt-4">
          <ProgressSteps label="Etapas del cambio" nodes={changeNodes(d)} />
        </div>

        {msg && <p role={msg.ok ? 'status' : 'alert'} className={`rounded-md px-3 py-2 text-sm ${msg.ok ? 'bg-accent-soft text-accent-dark' : 'bg-[#FBEAEA] text-[#8F1D1D]'}`}>{msg.text}</p>}
        {!d.permissions.manage && d.stage === 'manager_review' && (
          <p className="rounded-md bg-[#FFF4E5] px-3 py-2 text-sm text-[#6A3A00]">Estimación enviada: ahora la revisa el gestor del servicio.</p>
        )}
        {!d.permissions.manage && d.stage === 'pending_approval' && (
          <p className="rounded-md bg-[#FFF4E5] px-3 py-2 text-sm text-[#6A3A00]">Cotización enviada al cliente; esperando su aprobación.</p>
        )}
        {d.permissions.manage && d.stage !== 'estimating' && d.versions.length > 0 && (
          <ChangeQuotePanel d={d} onDone={onDone} onError={onError} />
        )}
        {d.evolutive && <EvolutivePanel d={d} onMessage={(ok, text) => setMsg({ ok, text })} />}
        {DELIVERY_STAGES.includes(d.stage) && <ChangeDeliveryPanel d={d} onDone={onDone} onError={onError} />}

        <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_260px]">
          <section className="flex flex-col gap-5 rounded-lg border border-line bg-white p-4 sm:p-5">
            {!estimateOpen ? (
              <button type="button" className="flex w-full flex-wrap items-center justify-between gap-2 text-left" aria-expanded="false" onClick={() => setShowEstimate(true)}>
                <span className="text-base font-semibold">Estimación del consultor</span>
                <span className="text-sm text-muted">{d.versions[0] ? `v${d.versions[0].version} · ${d.versions[0].consultantTotal} h` : `${live.totals.total} h`} · <span className="text-accent underline">Ver</span></span>
              </button>
            ) : (<>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-base font-semibold">Estimación del consultor
                {!inEstimation && <button type="button" className="ml-3 text-sm font-normal text-accent underline" aria-expanded="true" onClick={() => setShowEstimate(false)}>Ocultar</button>}
              </h2>
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.requiresAbap} disabled={!editable} onChange={e => set({ requiresAbap: e.target.checked })} />
                Requiere ABAP
              </label>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[460px] border-collapse text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-muted">
                    <th scope="col" className="border-b border-line py-2 pr-3 font-semibold">Rubro</th>
                    <th scope="col" className="w-28 border-b border-line px-2 py-2 text-right font-semibold">Funcional</th>
                    {form.requiresAbap && <th scope="col" className="w-28 border-b border-line px-2 py-2 text-right font-semibold">ABAP</th>}
                    <th scope="col" className="w-20 border-b border-line py-2 pl-2 text-right font-semibold">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {d.phases.map(p => (
                    <tr key={p.id} className="border-b border-[#EFEDE9]">
                      <th scope="row" className="py-1.5 pr-3 text-left font-medium">{p.name}</th>
                      {(['functional', ...(form.requiresAbap ? ['abap'] : [])] as Discipline[]).map(disc => (
                        <td key={disc} className="px-2 py-1.5">
                          <input type="number" min="0" step="0.5" aria-label={`${p.name} ${disc === 'abap' ? 'ABAP' : 'Funcional'}`} disabled={!editable}
                            className={`${field} text-right tabular-nums`} value={hours(p.id, disc) || ''} placeholder="0"
                            onChange={e => setHours(p.id, disc, Math.max(0, Number(e.target.value) || 0))} />
                        </td>
                      ))}
                      <td className="py-1.5 pl-2 text-right font-mono tabular-nums">{phaseTotal(p.id) || '—'}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-semibold">
                    <td className="py-2 pr-3">Total</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums">{live.totals.functional}</td>
                    {form.requiresAbap && <td className="px-2 py-2 text-right font-mono tabular-nums">{live.totals.abap}</td>}
                    <td className="py-2 pl-2 text-right font-mono text-base tabular-nums">{live.totals.total} h</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <p className="flex flex-wrap items-center gap-2 text-xs text-muted">
              <span className={`rounded-md px-2 py-1 font-semibold ${CLASS_TONE[live.cls]}`}>Preliminar: {BILLING_CLASS_LABEL[live.cls]}</span>
              Orientativa: la clasificación definitiva la da la cotización del gestor (subtotal con riesgo).
            </p>

            {form.requiresAbap && (
              <label className="flex max-w-sm flex-col gap-1 text-sm">ABAP estimado por (opcional)
                <select className={field} disabled={!editable} value={form.abapEstimatedById ?? ''} onChange={e => set({ abapEstimatedById: e.target.value || null })}>
                  <option value="">—</option>
                  {meta.data?.agents.map(a => <option key={a.uid} value={a.uid}>{a.name}</option>)}
                </select>
              </label>
            )}

            <label className="flex flex-col gap-1 text-sm font-medium">Alcance
              <textarea rows={4} className={area} disabled={!editable} value={form.scope} onChange={e => set({ scope: e.target.value })} />
            </label>

            <fieldset className="flex flex-col gap-2">
              <legend className="mb-1 text-sm font-medium">Resultados esperados / criterios de aceptación <span className="font-normal text-muted">(serán la lista de la UAT)</span></legend>
              {form.acceptanceCriteria.map((c, i) => (
                <div key={i} className="flex gap-2">
                  <span className="mt-2 w-5 text-right font-mono text-xs text-muted">{i + 1}.</span>
                  <input className={field} disabled={!editable} value={c} aria-label={`Criterio ${i + 1}`}
                    onChange={e => set({ acceptanceCriteria: form.acceptanceCriteria.map((x, j) => (j === i ? e.target.value : x)) })} />
                  {editable && <button type="button" className="text-xs text-muted hover:text-[#8F1D1D]" onClick={() => set({ acceptanceCriteria: form.acceptanceCriteria.filter((_, j) => j !== i) })}>Quitar</button>}
                </div>
              ))}
              {editable && <div><button type="button" className={btnSecondary} onClick={() => set({ acceptanceCriteria: [...form.acceptanceCriteria, ''] })}>+ Agregar criterio</button></div>}
            </fieldset>

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1 text-sm font-medium">Fuera de alcance / limitaciones
                <textarea rows={3} className={area} disabled={!editable} value={form.outOfScope} onChange={e => set({ outOfScope: e.target.value })} />
              </label>
              <label className="flex flex-col gap-1 text-sm font-medium">Supuestos
                <textarea rows={3} className={area} disabled={!editable} value={form.assumptions} onChange={e => set({ assumptions: e.target.value })} />
              </label>
            </div>
            <label className="flex max-w-xs flex-col gap-1 text-sm font-medium">Entrega (días hábiles desde la aprobación)
              <input type="number" min="0" className={field} disabled={!editable} value={form.commitmentBusinessDays || ''} onChange={e => set({ commitmentBusinessDays: Math.max(0, Math.round(Number(e.target.value) || 0)) })} />
            </label>

            {editable && (
              <div className="flex flex-col gap-2 border-t border-line pt-4">
                {live.problems.length > 0 && <p className="text-xs text-muted">Para enviarla falta: {live.problems.join(' · ')}.</p>}
                <div className="flex flex-wrap gap-2">
                  <button type="button" className={btnSecondary} disabled={saveDraft.isPending} onClick={() => saveDraft.mutate()}>Guardar borrador</button>
                  <button type="button" className={btnPrimary} disabled={submit.isPending || live.problems.length > 0} onClick={() => submit.mutate()}>Enviar al gestor del servicio</button>
                </div>
              </div>
            )}
            </>)}
          </section>

          <aside className="flex flex-col gap-4">
            <section className="rounded-lg border border-line bg-white p-4 text-sm">
              <h2 className="text-sm font-semibold">Versiones enviadas</h2>
              {d.versions.length === 0 ? <p className="mt-2 text-xs text-muted">Aún no se envía ninguna.</p> : (
                <ul className="mt-2 space-y-2">
                  {d.versions.map(v => (
                    <li key={v.version} className="text-xs">
                      <span className="font-semibold">v{v.version}</span> · {v.consultantTotal} h ({v.functionalTotal} F{v.abapTotal ? ` + ${v.abapTotal} ABAP` : ''})
                      <span className="block text-muted">{v.submittedBy.name} · {dateTime(v.submittedAt)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <section className="rounded-lg border border-line bg-white p-4 text-sm">
              <h2 className="text-sm font-semibold">Solicitud del cliente</h2>
              <p className="mt-2 whitespace-pre-line text-xs leading-relaxed text-[#3F3D39]">{d.ticket.description}</p>
            </section>
          </aside>
        </div>
      </div>
    </Shell>
  );
}
